<template>
    <div>
        <table-page
            v-show="!addingDelivery"
            :client="client"
            @updated="getDeliveries"
            :warnings="warnings"
            :changelogs="changelogs"
            :hideTabs="true"
        >
            <fast-table
                :recordLabel="recordLabel"
                :sections="sections"
                :headers="headers"
                :data="tableData"
                :activeRowId="currentDeliveryId"
                @click="currentDeliveryId = $event.id"
            >
                <template v-slot:table-actions>
                    <button class="btn btn-primary btn-collapse" id="addDeliveryButton" @click="addDelivery">
                        <i class="far fa-fw fa-plus"></i> <span class="btn-text">Add Delivery Address</span>
                    </button>
                </template>
            </fast-table>

        </table-page>

        <div v-if="addingDelivery" class="flex-body body-scroll">
            <div class="container-fluid">
                <div class="container-centered-m py-4">
                    <h3>Add Delivery Address</h3>
                    <template v-if="addDeliveryStep == 'delivery'">
                        <p>Input the information below for this new delivery address.</p>
                        <div class="card mb-2">
                            <div class="card-header pr-1 d-flex align-items-center">
                                <span class="btnlike mr-auto">
                                    <h4 class="mb-0">
                                        Delivery Address
                                    </h4>
                                </span>
                            </div>
                            <div class="card-body">
                                <form>
                                    <form-input label="Care of" v-model="formData.delivery_address.care_of" :errors="errors.care_of" type="text" :maxlength="100"/>
                                    <form-input label="Address 1" v-model="formData.delivery_address.address_1" :errors="errors.address_1" type="text" :maxlength="100"/>
                                    <form-input label="Address 2 (Optional)" v-model="formData.delivery_address.address_2" :errors="errors.address_2" type="text" :maxlength="100"/>
                                    <form-input label="City" v-model="formData.delivery_address.city" :errors="errors.city" type="text" :maxlength="100"/>
                                    <div class="form-row">
                                        <div class="col-6">
                                            <form-input label="State" v-model="formData.delivery_address.state" :errors="errors.state" type="select" :options="stateOptions"/>
                                        </div>

                                        <div class="col-6">
                                            <form-input label="Zip Code" :maxlength="5" v-model="formData.delivery_address.postal_code" :errors="errors.postal_code" type="text" inputmode="tel" />
                                        </div>
                                    </div>
                                    <form-input label="Friendly Name" v-model="formData.delivery_address.name" :errors="errors.name" type="text" :maxlength="100"/>

                                    <h5 class="mt-2">Methods</h5>

                                    <p>Select this company's preferred <strong>carrier</strong>:</p>
                                    <form-input type="radio" v-model="formData.delivery_address.carrier" @input="onCarrierSelect" :errors="errors.carrier" :options="deliveryCarrierOptions" class="pl-0"/>
                                    <form-input label="Method" v-if="deliveryMethodOptionsByCarrier[formData.delivery_address.carrier].length" type="select" v-model="formData.delivery_address.method" :errors="errors.method" :options="deliveryMethodOptionsByCarrier[formData.delivery_address.carrier]" class="mt-1 mb-2"/>

                                    <template v-if="formData.delivery_address.carrier == 'ups' || formData.delivery_address.carrier == 'fedex' ">
                                        <p>Whose account will this company use with this carrier?</p>
                                        <div class="form-check-set">
                                            <div class="form-check d-inline-block mr-2">
                                                <label>
                                                    <input name="use_own_account" type="radio" @click="formData.delivery_address.use_own_account = false" :checked="formData.delivery_address.use_own_account == false" class="form-check-input">
                                                    Paymasters
                                                </label>
                                            </div>
                                            <div class="form-check d-inline-block">
                                                <label>
                                                    <input name="use_own_account" type="radio" @click="formData.delivery_address.use_own_account = true" :checked="formData.delivery_address.use_own_account == true" class="form-check-input">
                                                    Their own account
                                                </label>
                                            </div>
                                        </div>
                                        <div v-if="formData.delivery_address.use_own_account" class="mt-1">
                                            <form-input label="Name on the Account" v-model="formData.delivery_address.account_name" :errors="errors.account_name" type="text" :maxlength="100"/>
                                            <form-input label="Account Number" v-model="formData.delivery_address.account_number" :errors="errors.account_number" type="text" :maxlength="100"/>
                                        </div>
                                    </template>

                                    <template v-if="formData.delivery_address.carrier == 'courier'">
                                        <form-input label="Instructions" v-model="formData.delivery_address.instructions" :errors="errors.instructions" type="text" :nocaps="true"/>
                                    </template>

                                    <form-input label="Use this as the default delivery address for this company" v-model="formData.delivery_address.is_default" :errors="errors.is_default" type="checkbox" class="mt-2"/>


                                </form>
                            </div>
                        </div>
                    </template>

                    <template v-else-if="addDeliveryStep == 'company'">
                        <p class="mb-hf">Assign the companies this address is available for.</p>
                        <div class="p-1 unpad-x bg-150 rounded">
                            <div><span class="text-muted">Delivery Address: </span>{{ formData.delivery_address.name }}</div>
                            <div><span class="text-muted">Method: </span>{{ selectedMethodPretty }}</div>
                        </div>
                        <company-selector
                            :client="client"
                            :companies="client.companies"
                            v-model="formData.companies"
                        />
                    </template>

                    <form>
                        <form-errors :errors="formErrors"/>

                        <div class="row mb-2 d-flex align-items-center">
                            <div class="col">
                                <button v-if="addDeliveryStep == 'delivery'" class="btn-outline-primary btn" @click.prevent="addingDelivery = false; formData = makeFormData()">Back</button>
                                <button v-else-if="addDeliveryStep == 'company'" class="btn-outline-primary btn" @click.prevent="addDeliveryStep = 'delivery'">Back</button>
                            </div>

                            <div class="col text-right">
                                <button v-if="addDeliveryStep == 'delivery'" class="btn-primary btn" @click.prevent="addDeliveryStep = 'company'">Continue</button>
                                <button v-else class="btn btn-primary" @click.prevent="onSubmit">Save</button>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </div>

        <delivery-sidebar
            v-if="currentDelivery"
            :client="client"
            :delivery-id="currentDelivery.id"
            ref="deliverySidebar"
            @updated="getDeliveries"
            @close="currentDeliveryId = null"
        >
        </delivery-sidebar>
    </div>
</template>

<script>
import TablePageMixin from '@/mixins/TablePageMixin'
import FastTable from '@/components/FastTable'
import DeliverySidebar from './components/Deliveries/DeliverySidebar'
import CompanySelector from '@/pages/client/Settings/components/CompanySelector'
import STATES from '@/states'

const deliveryCarrierOptions = [
    {text: 'UPS', value: 'ups'},
    {text: 'FedEx', value: 'fedex'},
    {text: 'Courier (ProMed)', value: 'courier'},
    {text: 'Pickup', value: 'pickup'},
]

const deliveryMethodOptionsByCarrier = {
    'fedex': [
        {text: 'FedEx Standard Overnight', value: 'fedex-standard-overnight'},
        {text: 'FedEx 2Day', value: 'fedex-2day'},
        {text: 'FedEx Ground', value: 'fedex-ground'},
    ],
    'ups': [
        {text: 'UPS Standard Overnight', value: 'ups-standard-overnight'},
        {text: 'UPS 2Day', value: 'ups-2day'},
        {text: 'UPS Ground', value: 'ups-ground'},
    ],
    'courier': [],
    'pickup': [],
    '': [],
}

export default {
    components: {FastTable, DeliverySidebar, CompanySelector},
    mixins: [TablePageMixin],
    props: ['client'],
    computed: {
        currentDelivery() {
            return this.deliveries.find(e => (e.id == this.currentDeliveryId))
        },
        sections() {
            let sections = [
                {title: 'Active', defaultIsClosed: false, id: 'active', hasDateFilter: false},
                {title: 'Inactive', defaultIsClosed: true, id: 'inactive', hasDateFilter: true},
            ]
            return sections
        },
        activeDeliveries() {
            return this.deliveries.filter((d) => {return d.is_active})
        },
        inactiveDeliveries() {
            return this.deliveries.filter((d) => {return !d.is_active})
        },
        tableData() {
            const tableData = []
            const subsets = [
                this.activeDeliveries,
                this.inactiveDeliveries,
            ]

            subsets.forEach(deliveries => {
                const sectionData = []

                deliveries.forEach(delivery => {
                    sectionData.push({
                        id: delivery.id,
                        object: delivery,
                        cells: this.makeDeliveryTableData(delivery),
                    })
                })
                tableData.push(sectionData)
            })
            return tableData
        },
        stateOptions() {
            const opts = []
            STATES.forEach(s => {
                opts.push({text: s.name.toUpperCase(), value: s.abbreviation})
            })
            return opts
        },
        selectedMethodPretty() {
            let carrier = deliveryCarrierOptions.find(m => m.value == this.formData.delivery_address.carrier)
            let method = deliveryMethodOptionsByCarrier[this.formData.delivery_address.carrier].find(m => m.value == this.formData.delivery_address.method)
            if (method) {
                return method.text
            }
            if (carrier) {
                return carrier.text
            }
            return ''
        },
    },
    data() {
        return {
            deliveries: [],
            addingDelivery: false,
            addDeliveryStep: 'delivery',
            dataLoaded: false,
            currentDeliveryId: null,
            recordLabel: "Delivery Addresses",
            headers: [
                {label: 'Name', classes: 'cw-12', isSearchable: true, defaultSort: true},
                {label: 'Company', classes: 'cell-company', isFilterable: true, isSearchable: true, defaultSort: false},
                {label: 'City', classes: '', isSearchable: true, isFilterable: true},
                {label: 'State', classes: 'cs-6', isFilterable: true, isSearchable: true, defaultSort: false},
                {label: 'Carrier', classes: '', isFilterable: true, isSearchable: true, defaultSort: false},
                {label: 'Method', classes: 'cw-12', isFilterable: true, isSearchable: true, defaultSort: false},
                {label: 'Default', classes: '', type: "checkmark", isFilterable: true, isSearchable: false, defaultSort: false},
                {label: 'ID', classes: 'cw-18', type: "copy", isFilterable: false, isSearchable: false, defaultSort: false},
            ],
            formData: this.makeFormData(),
            errors: [],
            formErrors: [],
            deliveryCarrierOptions: deliveryCarrierOptions,
            deliveryMethodOptionsByCarrier: deliveryMethodOptionsByCarrier,
        }
    },
    mounted() {
        this.$store.dispatch('SET_PAGE_TITLE', 'Settings / Deliveries')
        this.getDeliveries()
    },
    methods: {
        getDeliveries() {
            this.$store.dispatch('START_LOADING')
            this.$api.get(`/clients/${this.client.id}/settings/delivery-addresses`).then(resp => {
                this.deliveries = resp
                this.dataLoaded = true
                this.$store.dispatch('STOP_LOADING')
            }).catch((errors) => {
                this.$store.dispatch('STOP_LOADING')
                if (errors.__status__ == 403) {
                    this.$bus.$emit('no-access')
                }
                else {
                    this.$bus.showError(errors.__all__)
                }
            })
        },
        makeDeliveryTableData(delivery) {
            let companyName = this.client.companies.find(c => c.id == delivery.company_id).name
            return [
                delivery.name,
                companyName,
                delivery.city,
                delivery.state,
                delivery.carrier_pretty,
                delivery.method_pretty,
                delivery.is_default,
                delivery.public_id,
            ]
        },
        addDelivery() {
            this.addingDelivery = true
        },
        onCarrierSelect() {
            this.formData.delivery_address.method = ''
        },
        makeDeliveryFormData() {
            return {
                care_of: '',
                name: '',
                address_1: '',
                address_2: '',
                city: '',
                state: '',
                postal_code: '',
                carrier: '',
                method: '',
                use_own_account: null,
                account_name: '',
                account_number: '',
                instructions: '',
                is_default: false,
            }
        },
        makeFormData() {
            return {
                companies: [],
                delivery_address: this.makeDeliveryFormData(),
            }
        },
        getFormData() {
            return this.formData
        },
        onSubmit() {
            this.$store.dispatch('START_LOADING')
            return this.$api.post(`/clients/${this.client.id}/settings/delivery-addresses`, this.getFormData()).then(resp => {
                this.$store.dispatch('STOP_LOADING')
                this.deliveries = resp
                this.addingDelivery = false
                this.formData = this.makeFormData()
                this.addDeliveryStep = 'delivery'
                this.$emit('updated')

            }).catch((errors) => {
                this.$store.dispatch('STOP_LOADING')

                this.formErrors = (errors.__all__ || [])
                this.$bus.showError('Please check the form for any missing fields.')
            })
        },
    },
}
</script>
